import type { RouteLocationNormalized, Router } from 'vue-router';

// Start a route's lazy chunks with its guards, not after them (issue 533).
// Installed once per app in router/index.ts, BEFORE the app's own guard:
// `installRouteChunkPrefetch(router)`.
//
// vue-router downloads a lazy route component (`() => import(...)`) only in its
// `beforeRouteEnter` phase, which runs after EVERY `beforeEach` guard has settled.
// The apps' guards await the session, the onboarding status and the active role
// before that, so on every full page load (a module, role or demo persona switch)
// the view's chunk was the last thing to start — one more serial round trip
// before the first paint. Calling the same loader from the first guard starts the
// download alongside those reads; the router's own call later finds the module
// already loading (or loaded) and awaits that instead of fetching it again.
//
// A loader that fails here fails again when the router calls it, and that is the
// path that reports it (installChunkReloadRecovery), so a failure is dropped here.

type Loader = () => unknown;

// vue-router's own test for "this is a component, not a loader" — anything else
// that is a function, it calls as a lazy loader, so this calls exactly the same.
function isLazyLoader(component: unknown): component is Loader {
  if (typeof component !== 'function') return false;
  return !('displayName' in component) && !('props' in component) && !('__vccOpts' in component);
}

export function prefetchRouteChunks(to: Pick<RouteLocationNormalized, 'matched'>): void {
  for (const record of to.matched) {
    for (const component of Object.values(record.components ?? {})) {
      if (!isLazyLoader(component)) continue;
      try {
        const pending = component();
        if (pending && typeof (pending as Promise<unknown>).then === 'function') {
          (pending as Promise<unknown>).then(undefined, () => {});
        }
      } catch {
        // The router's own call will throw the same and report it.
      }
    }
  }
}

export function installRouteChunkPrefetch(router: Pick<Router, 'beforeEach'>): void {
  router.beforeEach((to) => {
    prefetchRouteChunks(to);
  });
}
